import React, { useEffect, useMemo, useState } from 'react';
import { useUser } from '../hooks/useUser';
import { acceptInvite, getInvite, isApiError } from '../services/api';

/**
 * Page d'inscription par invitation.
 *
 * L'email d'invitation ET le lien copiable du tableau de bord admin pointent vers
 * `/accept-invite?token=…` : cette route n'existait pas côté frontend, un étudiant qui
 * cliquait le lien tombait sur une page BLANCHE (aucune route ne correspondait, `<main>`
 * restait vide). C'est la porte d'entrée de la promo quand aucun SMTP n'est configuré.
 */
function useQueryParam(name: string) {
  return useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get(name) || '';
  }, [name]);
}

const AcceptInvitePage: React.FC = () => {
  const tokenRaw = useQueryParam('token');
  // Même nettoyage que ResetPasswordPage : les tokens sont hexadécimaux, on retire les
  // guillemets et espaces qu'un copier-coller depuis un mail ajoute parfois.
  const token = useMemo(
    () => tokenRaw.trim().replace(/^"+|"+$/g, '').replace(/[^a-fA-F0-9]/g, '').toLowerCase(),
    [tokenRaw]
  );
  const { refreshUser } = useUser();

  const [checking, setChecking] = useState(true);
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [inviteError, setInviteError] = useState<string>('');
  const [pseudonym, setPseudonym] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (!token) {
        setInviteError('Lien d\'invitation invalide ou incomplet.');
        setChecking(false);
        return;
      }
      try {
        const invite = await getInvite(token);
        if (cancelled) return;
        setInviteEmail(invite.email);
      } catch (error) {
        if (cancelled) return;
        if (isApiError(error) && !error.isNetworkError) {
          setInviteError(error.message || 'Invitation invalide.');
        } else {
          setInviteError('Serveur injoignable. Réessayez dans un instant.');
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirm) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setIsLoading(true);
    try {
      await acceptInvite({ token: token ?? '', password, pseudonym: pseudonym.trim() || undefined });
      // Le cookie de session est posé par la réponse : on recharge l'identité puis
      // on part sur l'accueil (rechargement complet pour repartir d'un état propre).
      refreshUser();
      window.location.href = '/';
    } catch (error) {
      if (isApiError(error) && !error.isNetworkError) {
        const details = error.details.length > 0 ? ` (${error.details.join(', ')})` : '';
        setError(`${error.message || 'Impossible de créer le compte.'}${details}`);
      } else {
        setError('Erreur de connexion au serveur. Réessayez.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="max-w-md mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow p-6 text-gray-600 dark:text-gray-300">
        Vérification de votre invitation...
      </div>
    );
  }

  if (inviteError) {
    return (
      <div className="max-w-md mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow p-6">
        <h1 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Invitation indisponible</h1>
        <div className="mb-4 p-3 rounded bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-sm">
          {inviteError}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Demandez un nouveau lien à votre formateur, ou connectez-vous si vous avez déjà un compte.
        </p>
        <div className="mt-4 text-sm">
          <a href="/login" className="text-blue-600 dark:text-blue-400 hover:underline">Aller à la connexion</a>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow p-6">
      <h1 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">Créer mon compte</h1>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Invitation pour <strong className="text-gray-900 dark:text-white">{inviteEmail}</strong>. Choisissez un
        pseudonyme et un mot de passe : c'est avec cette adresse que vous vous connecterez ensuite.
      </p>

      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-sm">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="pseudonym" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Pseudonyme
          </label>
          <input
            id="pseudonym"
            type="text"
            value={pseudonym}
            onChange={(e) => setPseudonym(e.target.value)}
            placeholder="Comment doit-on vous appeler ?"
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            disabled={isLoading}
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Mot de passe
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mot de passe"
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            required
            disabled={isLoading}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            8+ caractères, avec majuscules, minuscules et chiffres
          </p>
        </div>

        <div>
          <label htmlFor="confirm" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Confirmer le mot de passe
          </label>
          <input
            id="confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirmez le mot de passe"
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            required
            disabled={isLoading}
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? 'Création du compte...' : 'Créer mon compte'}
        </button>
      </form>

      <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
        Déjà inscrit ?{' '}
        <a href="/login" className="text-blue-600 dark:text-blue-400 hover:underline">Se connecter</a>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
